import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { fetchUserProfile } from '../services/userService';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface JwtPayload {
  exp: number;
}

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const useUserProfile = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const userEmail = localStorage.getItem('userEmail');

  const { data: userData, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['userProfile', userId],
    queryFn: async () => {
      if (!token || !userId) {
        throw new Error('No authentication token or user ID found');
      }

      if (isTokenExpired(token)) {
        localStorage.removeItem('token');
        throw new Error('Authentication token has expired');
      }

      try {
        const data = await fetchUserProfile(userId);
        return {
          ...data,
          email: userEmail || data.email || '',
        };
      } catch (err: unknown) {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 403 || err.response?.status === 401) {
            navigate('/login');
          }
          throw new Error(
            err.response?.status === 403
              ? 'Unauthorized access to user data'
              : `Failed to fetch user data: ${err.response?.status || 'Unknown'}`
          );
        }
        if (err instanceof Error) {
          throw err;
        }
        throw new Error('Failed to fetch user profile');
      }
    },
    enabled: !!token && !!userId,
    retry: false, // Don't retry auth errors usually
  });

  const error = queryError ? (queryError as Error).message : null;

  const refreshProfile = async () => {
    await queryClient.invalidateQueries({ queryKey: ['userProfile', userId] });
  };

  // Immediate redirect for unauthenticated users
  if (!loading && (!token || !userId)) {
    navigate('/login');
  }

  return { userData, loading, error, refreshProfile };
};

